import React from 'react';

import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { CookiesProvider } from 'react-cookie';
import { Provider as ReduxStoreProvider } from 'react-redux';
import { RouterProvider } from 'react-router';

import ResponseMessage from '@/components/ResponseMessage';
import { darkModeTheme } from '@/constants/mui';
import { SnackbarNotificationsProvider } from '@/context/SnackbarNotificationsContext';
import { AddToAppContextProvider } from '@/features/Dashboard/context/AppContext';
import { useFeatureFlagRefresh } from '@/hooks/useFeatureFlagRefresh';
import useImpostorRefresh from '@/hooks/useImpostorRefresh';

import { router } from './AppRoutes';
import { store } from './store';

const queryClient = new QueryClient();

const App: React.FC = () => {
  useImpostorRefresh();
  useFeatureFlagRefresh();
  return (
    <CookiesProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={darkModeTheme}>
          <ReduxStoreProvider store={store}>
            <AddToAppContextProvider>
              <SnackbarProvider
                Components={{
                  loading: ResponseMessage,
                  error: ResponseMessage,
                  success: ResponseMessage,
                  info: ResponseMessage
                }}
                maxSnack={5}
              >
                <SnackbarNotificationsProvider>
                  <CssBaseline />
                  <GlobalStyles
                    styles={{ '.notistack-SnackbarContainer': { zIndex: '100000 !important' } }}
                  />
                  <RouterProvider router={router} />
                </SnackbarNotificationsProvider>
              </SnackbarProvider>
            </AddToAppContextProvider>
          </ReduxStoreProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </CookiesProvider>
  );
};

export default App;
